import { useState } from 'react';
import { useFetcher } from 'react-router';

import { cn } from '@rocco/utils/cn';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-right.svg';
import { useFeature } from '~/modules/root';
import { SubscribePublicationsModal } from '@modules-rocco/subscription/component/SubscribePublicationsModal';

export interface B2BSubscribeFormProps {
    label: string;
    className?: string;
}

/**
 * 440 × 54 pill at design width → 22.917vw × 2.813vw, px 35 / py 19 → 1.823vw /
 * 0.99vw. Sized in `vw` so the proportions match the surrounding 1920 grid of the
 * spec footer (Figma `517:24697`); falls back to a fluid full-width pill on mobile.
 */
const PILL_CLASS =
    'flex h-[2.813vw] w-[22.917vw] items-center justify-between rounded-full bg-black/5 px-[1.823vw] py-[0.99vw] max-md:h-12 max-md:w-full max-md:px-6 max-md:py-3';

const TEXT_CLASS =
    'font-sans text-[0.833vw] font-medium tracking-[0.02em] text-[#141414] max-md:text-sm';

/**
 * Footer subscribe control.
 *
 * Behind `digital_publication_subscribe_2026` it is a button opening the publication
 * chooser: that collects the address itself, so asking for it here first only asks
 * twice. With the flag off it is the original email field, which subscribes on submit
 * and hands the reader every list in the footer bundle.
 */
export const B2BSubscribeForm = ({
    label,
    className,
}: B2BSubscribeFormProps) => {
    const canChoosePublications = useFeature(
        'digital_publication_subscribe_2026',
    );

    return canChoosePublications ? (
        <ChooserTrigger label={label} className={className} />
    ) : (
        <LegacySubscribeField className={className} />
    );
};

const ChooserTrigger = ({
    label,
    className,
}: {
    label: string;
    className?: string;
}) => {
    const [open, setOpen] = useState(false);
    /** Not mounted until first wanted; this sits in the B2B footer. */
    const [hasOpened, setHasOpened] = useState(false);

    return (
        <>
            <button
                type="button"
                onPointerEnter={() => setHasOpened(true)}
                onFocus={() => setHasOpened(true)}
                onClick={() => {
                    setHasOpened(true);
                    setOpen(true);
                }}
                className={cn(
                    PILL_CLASS,
                    'transition-colors hover:bg-black/10',
                    className,
                )}
            >
                <span className={TEXT_CLASS}>{label}</span>
                <ArrowRight
                    className="h-[0.686vw] w-[0.973vw] shrink-0 text-[#141414] max-md:size-4"
                    aria-hidden="true"
                />
            </button>
            {hasOpened ? (
                <SubscribePublicationsModal
                    open={open}
                    onClose={() => setOpen(false)}
                />
            ) : null}
        </>
    );
};

/**
 * The pre-flag field, kept whole so turning the flag off restores exactly what
 * shipped. Goes when the flag does.
 */
const LegacySubscribeField = ({ className }: { className?: string }) => {
    const fetcher = useFetcher<{ success?: boolean }>();
    const isSubmitting = fetcher.state !== 'idle';

    if (fetcher.data?.success === true) {
        return (
            <p
                className={cn(
                    'text-[0.833vw] text-[#141414] max-md:text-sm',
                    className,
                )}
                role="status"
            >
                You&apos;re subscribed — thanks for joining us.
            </p>
        );
    }

    return (
        <fetcher.Form
            method="post"
            action="/remix-api/user/subscription"
            className={cn(PILL_CLASS, className)}
        >
            <input
                type="email"
                name="email"
                required
                placeholder="Email Address"
                autoComplete="email"
                className={cn(
                    TEXT_CLASS,
                    'w-full bg-transparent placeholder:text-[#141414] focus:outline-hidden',
                )}
            />
            <button
                type="submit"
                aria-label="Subscribe"
                disabled={isSubmitting}
                className="shrink-0 text-[#141414] transition-opacity hover:opacity-60 disabled:opacity-40"
            >
                <ArrowRight className="h-[0.686vw] w-[0.973vw] max-md:size-4" />
            </button>
        </fetcher.Form>
    );
};
